import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Siren } from "lucide-react";
import { useReveal } from "../lib/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    is_emergency: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const ref = useReveal();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.subject || !form.message) {
      toast.error("Please fill name, subject and message");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        email: form.email || undefined,
        phone: form.phone || undefined,
      });
      toast.success(
        form.is_emergency ? "Emergency alert received" : "Message received",
        {
          description: form.is_emergency
            ? "Our rescue team will call you right away."
            : "We'll get back to you shortly.",
        }
      );
      setForm({ name: "", email: "", phone: "", subject: "", message: "", is_emergency: false });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#F4F1EA]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div ref={ref} className="reveal grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-[1px] w-10 bg-[#D4AF37]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#4A5D53]">
                Visit & contact
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#1A2E24] leading-[1.05]">
              Come, walk the <span className="italic text-[#1B4D3E]">pasture</span> with us.
            </h2>

            <div className="mt-10 space-y-5">
              <Row icon={MapPin} label="Our Address">
                Kherpur-Kadia Road, Opp. Lakshmipura,<br/>
                Ahmedabad – Mehsana Highway, Gujarat – 382705
              </Row>
              <Row icon={Phone} label="Helpline (24/7)">
                <a href="tel:+918141646161" className="hover:text-[#1B4D3E]">
                  +91 81416 46161
                </a>
              </Row>
              <Row icon={Mail} label="Email">
                <a href="mailto:hastkesarpanjrapole@gmail.com" className="hover:text-[#1B4D3E] break-all">
                  hastkesarpanjrapole@gmail.com
                </a>
              </Row>
              <Row icon={Clock} label="Visiting hours">
                Mon – Sun, 7:00 am – 6:30 pm
              </Row>
            </div>

            <a
              href="tel:+918141646161"
              data-testid="emergency-call-btn"
              className="mt-10 inline-flex items-center gap-3 bg-[#1B4D3E] hover:bg-[#143C30] text-[#FDFBF7] px-6 py-4 rounded-full font-medium"
            >
              <Siren className="w-4 h-4 text-[#D4AF37]" />
              Emergency — call rescue line
            </a>

            <div className="mt-10 rounded-2xl overflow-hidden border border-[#E3DECE] aspect-[16/10]">
              <iframe
                title="Our location on map"
                src="https://maps.google.com/maps?q=Kherpur-Kadia%20Road%2C%20Opp.%20Lakshmipura%2C%20Ahmedabad-Mehsana%20Highway%2C%20382705&t=&z=14&ie=UTF8&iwloc=&output=embed"
                className="w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="lg:col-span-7 bg-[#FDFBF7] border border-[#E3DECE] rounded-3xl p-8 md:p-12"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                data-testid="contact-name"
                className="bg-[#FDFBF7] border border-[#E3DECE] rounded-full px-5 py-3 outline-none focus:border-[#1B4D3E]"
              />
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Phone"
                data-testid="contact-phone"
                className="bg-[#FDFBF7] border border-[#E3DECE] rounded-full px-5 py-3 outline-none focus:border-[#1B4D3E]"
              />
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email (optional)"
                data-testid="contact-email"
                className="sm:col-span-2 bg-[#FDFBF7] border border-[#E3DECE] rounded-full px-5 py-3 outline-none focus:border-[#1B4D3E]"
              />
              <input
                required
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Subject"
                data-testid="contact-subject"
                className="sm:col-span-2 bg-[#FDFBF7] border border-[#E3DECE] rounded-full px-5 py-3 outline-none focus:border-[#1B4D3E]"
              />
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="How can we help?"
                data-testid="contact-message"
                className="sm:col-span-2 bg-[#FDFBF7] border border-[#E3DECE] rounded-2xl px-5 py-3 outline-none focus:border-[#1B4D3E] resize-none"
              />
              <label className="sm:col-span-2 flex items-center gap-3 text-sm text-[#4A5D53]">
                <input
                  type="checkbox"
                  checked={form.is_emergency}
                  onChange={(e) => setForm({ ...form, is_emergency: e.target.checked })}
                  data-testid="contact-emergency-check"
                  className="w-4 h-4 accent-[#1B4D3E]"
                />
                This is an animal emergency — please reach out urgently
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-btn"
              className="mt-8 inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#B5952F] disabled:opacity-60 text-[#1A2E24] font-semibold px-7 py-4 rounded-full transition-all"
            >
              {submitting ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-full bg-[#FDFBF7] border border-[#E3DECE] flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[#1B4D3E]" />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#7A8C81]">
          {label}
        </div>
        <div className="mt-1 text-[#1A2E24]">{children}</div>
      </div>
    </div>
  );
}
